import { Case, Client, DocumentStatus } from 'briefpoint-client';
import { FormModal } from 'components/Modals';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction, useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import { FileEarmarkPdf, FileEarmarkWord } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { DocumentClient, DocumentSubTypeDisplay } from '../../services/DocumentService';
import { formatShortDate } from '../../utils/dateUtils';
import DocumentActionsMenu from './DocumentActionsMenu';
import SelectCaseModal from './SelectCaseModal';
import useDocuments from 'hooks/useDocuments';

export function DocumentIcon({ extension }: { extension: string | undefined }) {
  if (!!extension && ['docx', 'doc'].includes(extension)) {
    return <FileEarmarkWord className="text-primary fs-2" />;
  }
  return <FileEarmarkPdf className="text-danger fs-2" />;
}

export function StartButton({ documentId }: { documentId: string }) {
  return (
    <Link
      className="btn btn-outline-primary btn-sm wide"
      to={`/wizard/${documentId}/source-document-review?document_type=false`}
    >
      Start
    </Link>
  );
}

export function ReviewButton({ documentId }: { documentId: string }) {
  return (
    <Link className="btn btn-outline-primary btn-sm wide" to={`/wizard/${documentId}`}>
      Review
    </Link>
  );
}

export function FinishButton({ documentId }: { documentId: string }) {
  return (
    <Link className="btn btn-outline-primary btn-sm wide" to={`/wizard/${documentId}`}>
      Continue
    </Link>
  );
}

export function FinishDraftButton({ document }: { document: DocumentClient }) {
  return (
    <Link className="btn btn-outline-primary btn-sm wide" to={`/clients/${document.clientId}/cases/${document.caseId}/generateDiscoveryRequest?documentId=${document.id}`}>
      {document.lastWordDownloadDate ? 'Edit' : 'Finish'}
    </Link>
  );
}

export function DownloadButton({ doc }: { doc: DocumentClient }) {
  const [, , , , , , , , downloadRequest] = useDocuments(false);

  return (
    <Button size='sm' variant='outline-primary wide' onClick={() => downloadRequest(doc)}>
      Download
    </Button>
  );
}

export function DueSoon(dueDate?: string) {
  if (!dueDate) return '​';

  const diff = DateTime.fromISO(dueDate).diff(DateTime.now(), 'days')

  if (diff.days < -1 || diff.days > 7) return '​';

  return (
    diff.days < 0 ? <Badge bg='danger'>Due Today</Badge> : <Badge bg='warning'>Due Soon</Badge>
  );
}

// convert from yyyy-mm-dd to mm/dd/yyyy
export function formatLocalDate(date?: string) {
  if (!date) return;

  var parts = date.split('-');

  return `${parts[1]}/${parts[2]}/${parts[0]}`
}

function DocumentRow({
  doc,
  _case,
  archiveDocument,
  editDueDate,
  setShowSetCase,
  downloadForm,
  downloadRequest
}: {
  doc: DocumentClient;
  _case?: Case;
  archiveDocument: (document: DocumentClient) => Promise<void>;
  editDueDate: Dispatch<SetStateAction<DocumentClient | undefined>>;
  setShowSetCase?: Dispatch<SetStateAction<DocumentClient | undefined>>;
  downloadForm: (document: DocumentClient, formId: string, formTypeName: string) => Promise<void>;
  downloadRequest: (document: DocumentClient) => Promise<void>;
}) {

  return (
    <tr>
      <td>
        <DocumentIcon extension="pdf" />
      </td>
      <td className='name'>{doc.name?.replace('.pdf', '').replace('.PDF', '')}</td>
      <td>{_case ? <Link title={_case!.shortTitle ?? ''} to={`/clients/${_case.clientId}/cases/${_case.id}`}>{_case!.shortTitle}</Link> : doc.info?.caseNumber ?? '-'}</td>
      <td>{formatShortDate(doc.uploadDate)}</td>
      <td>{doc.info?.documentSubType && DocumentSubTypeDisplay[doc.info.documentSubType].name}</td>
      <td>{formatLocalDate(doc.info?.dueDateOverridden || doc.info?.dueDate || undefined) || <Button className='set-dueDate-button' variant='link' onClick={() => editDueDate(doc)}>Set Due Date</Button>}</td>
      <td>{DueSoon(doc.info?.dueDateOverridden || doc.info?.dueDate || undefined)}</td>
      {/* <td>{doc.status === DocumentStatus.Complete ? 'Complete' : 'In Progress'}</td> */}
      <td className="text-end">
        {doc.status === DocumentStatus.Complete ? (
          <ReviewButton documentId={doc.id} />
        ) : (
          <FinishButton documentId={doc.id} />
        )}
      </td>
      <td className='kebab-cell'>
        <DocumentActionsMenu document={doc} _case={_case} archiveDocument={archiveDocument} editDueDate={editDueDate} setShowSetCase={setShowSetCase} downloadForm={downloadForm} downloadRequest={downloadRequest} />
      </td>
    </tr>
  );
}

export function sortDocumentDates(first: DocumentClient, second: DocumentClient, sortColumn: string, sortDirectionAscending: boolean) {
  // I couldn't get realDueDate as a computed property to work, sorry
  const a = ((sortColumn === 'realDueDate' ? toDateOrMin(first.info?.dueDateOverridden || first.info?.dueDate) : first[sortColumn as keyof typeof first] as Date) ?? new Date(-8640000000000000)).getTime();
  const b = ((sortColumn === 'realDueDate' ? toDateOrMin(second.info?.dueDateOverridden || second.info?.dueDate) : second[sortColumn as keyof typeof second] as Date) ?? new Date(-8640000000000000)).getTime();

  return sortDirectionAscending ? a - b : b - a;
}

export function toDateOrMin(val?: string | null) {
  return val ? new Date(val) : new Date(-8640000000000000);
}

export default function DocumentList({
  documents,
  archiveDocument,
  updateDocument,
  allCases,
  allClients,
  downloadForm,
  downloadRequest
}: {
  documents: DocumentClient[];
  archiveDocument: (document: DocumentClient) => Promise<void>;
  updateDocument: (document: DocumentClient, _case?: Case, client?: Client) => Promise<void>;
  allCases?: Case[],
  allClients?: Client[]
  downloadForm: (document: DocumentClient, formId: string, formTypeName: string) => Promise<void>;
  downloadRequest: (document: DocumentClient) => Promise<void>;

}) {
  const [showEditDueDate, setShowEditDueDate] = useState<DocumentClient | undefined>();
  const [showSetCase, setShowSetCase] = useState<DocumentClient | undefined>();
  const [dueDateOverride, setDueDateOverride] = useState<string | undefined>();
  const [sortColumn, setSortColumns] = useState('uploadDate');
  const [sortDirectionAscending, setSortDirectionAscending] = useState(false);

  function closeDueDateModal() {
    setShowEditDueDate(undefined);
    setDueDateOverride(undefined);
  }

  async function saveDocumentDueDate(showEditDueDate: DocumentClient): Promise<void> {
    showEditDueDate.info!.dueDateOverridden = dueDateOverride;
    await updateDocument(showEditDueDate);
    closeDueDateModal();
  }

  function toDateOrNow(val?: string | null) {
    return val ? DateTime.fromISO(val) : DateTime.now();
  }

  function changeSort(column: string) {
    if (column === sortColumn) {
      setSortDirectionAscending(!sortDirectionAscending);
    } else {
      setSortColumns(column);
      setSortDirectionAscending(false);
    }
  }

  function addDueDateDays(days?: number) {
    const existing = toDateOrNow(showEditDueDate?.info?.dueDateOverridden || showEditDueDate?.info?.dueDate);

    const newDate = days ? existing.plus({ days }) : existing.plus({ month: 1 });

    setDueDateOverride(newDate.toFormat('yyyy-MM-dd'));
  }

  return (
    <>
      <table id="document-list-table">
        <thead>
          <tr>
            <th></th>
            <th>Document Name</th>
            <th>Case</th>
            <th style={{ width: '93px' }} onClick={() => changeSort('uploadDate')} className={`clickable ${sortColumn === 'uploadDate' ? 'sorting' : ''} ${sortDirectionAscending ? 'ascending' : 'descending'}`}>Upload Date</th>
            <th>Document Type</th>
            <th onClick={() => changeSort('realDueDate')} className={`clickable ${sortColumn === 'realDueDate' ? 'sorting' : ''} ${sortDirectionAscending ? 'ascending' : 'descending'}`}>Due Date</th>
            <th></th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {documents.sort((a, b) => sortDocumentDates(a, b, sortColumn, sortDirectionAscending)).map((doc) => (
            <DocumentRow key={doc.id} doc={doc} archiveDocument={archiveDocument} editDueDate={setShowEditDueDate} _case={doc.caseId ? allCases?.find(x => x.id === doc.caseId) : undefined} setShowSetCase={doc.caseId ? undefined : setShowSetCase} downloadForm={downloadForm} downloadRequest={downloadRequest} />
          ))}
        </tbody>
      </table>
      <SelectCaseModal doc={showSetCase} onClose={() => setShowSetCase(undefined)} saveCaseClient={updateDocument} allCases={allCases} allClients={allClients} />
      <FormModal
        show={!!showEditDueDate}
        title={`${showEditDueDate?.info?.dueDateOverridden || showEditDueDate?.info?.dueDate ? 'Edit' : 'Set'} Due Date`}
        size='sm'
        confirmText='Confirm'
        cancelText='Cancel'
        onClose={closeDueDateModal}
        onConfirm={() => saveDocumentDueDate(showEditDueDate!)}
      >
        {(showEditDueDate?.info?.dueDateOverridden || showEditDueDate?.info?.dueDate) &&
          <div className='add-x-days'>
            Add <Button variant='link' onClick={() => addDueDateDays(7)} >7 days</Button>, <Button variant='link' onClick={() => addDueDateDays(14)}  >14 days</Button>, <Button onClick={() => addDueDateDays()} variant='link' >1 month</Button>
          </div>
        }
        <Form.Control type='date' className='date-picker' value={dueDateOverride || showEditDueDate?.info?.dueDateOverridden || showEditDueDate?.info?.dueDate || undefined}
          onChange={(e) => setDueDateOverride(e.target.value)} />
      </FormModal>
    </>
  );
}
